import {
  Callout,
  Card,
  FormGroup,
  InputGroup,
  ProgressBar,
} from "@blueprintjs/core";
import { MediaImage } from "@package/ui-apollo-components";
import { HeaderCreateEdit } from "@package/ui-components";
import {
  CreateMediaDocument,
  DeleteMediaDocument,
  GetMediaDocument,
  GetMediaDownloadUrlDocument,
  GetMediaGridDocument,
  GetMediaUploadUrlDocument,
  UpdateMediaDocument,
  useGetMediaQuery,
  type Media,
} from "@package/ui-gql/index-data-access";
import { useUpdateSelectedOrganization } from "@package/ui-organization";
import { produce, type WritableDraft } from "immer";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  EDIT_CARD_WIDTH_CLASS,
  SidebarEntryPath,
} from "../../../../constants/contants";
import { SaveToolbar } from "../../../SaveToolbar/SaveToolbar";

const defaultMedia: Media = {
  isDir: false,
  name: "",
  organization: "",
};

export function MediaEdit() {
  const [mediaState, setMediaState] = useState<Media>(defaultMedia);
  const [disabled, setDisabled] = useState<boolean>(false);
  const { id } = useParams();

  const updateMedia = useCallback(
    (callback: (draft: WritableDraft<Media>) => void) => {
      setMediaState((prev) => produce(prev, callback));
    },
    [],
  );

  const {
    data: dataMedia,
    loading: loadingMedia,
    error: errorMedia,
  } = useGetMediaQuery({
    variables: {
      id: id ?? "",
    },
    skip: !id,
  });

  useEffect(
    function effectOnMediaDataChanged() {
      if (dataMedia?.item) {
        setMediaState(dataMedia.item);
      }
    },
    [dataMedia?.item],
  );

  useUpdateSelectedOrganization(dataMedia?.item?.organization);

  if (loadingMedia) {
    return (
      <Card elevation={1}>
        <ProgressBar intent="primary" stripes animate />
      </Card>
    );
  }

  if (errorMedia) {
    return (
      <Callout intent="danger" title="Failed Loading Media">
        <p>Failed loading media item: {id}</p>
      </Callout>
    );
  }

  return (
    <div className="flex flex-col">
      <HeaderCreateEdit
        editing={!!id}
        loading={loadingMedia}
        title="Media Library"
        name={dataMedia?.item?.name}
      />
      <Card elevation={1} className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            label="Name"
            labelFor="control-name"
            labelInfo="(required)"
            disabled={disabled}
          >
            <InputGroup
              disabled={disabled}
              id="control-name"
              placeholder="Name"
              value={mediaState.name ?? ""}
              onValueChange={(value) =>
                updateMedia((draft) => {
                  draft.name = value;
                })
              }
            />
          </FormGroup>
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup label="File" labelFor="control-file" disabled={true}>
            <InputGroup
              disabled={true}
              id="control-file"
              placeholder="File"
              value={mediaState.file ?? ""}
            />
          </FormGroup>
          <FormGroup label="Size" labelFor="control-size" disabled={true}>
            <InputGroup
              disabled={true}
              id="control-size"
              placeholder="Size"
              value={mediaState.size?.toString() ?? ""}
            />
          </FormGroup>
          <FormGroup label="Type" labelFor="control-type" disabled={true}>
            <InputGroup
              disabled={true}
              id="control-type"
              placeholder="Type"
              value={mediaState.type ?? ""}
            />
          </FormGroup>
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <MediaImage id={id} size="max-h-full" />
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`flex ${EDIT_CARD_WIDTH_CLASS}`}>
          <SaveToolbar
            documentCreate={CreateMediaDocument}
            documentDelete={DeleteMediaDocument}
            documentUpdate={UpdateMediaDocument}
            path={SidebarEntryPath.media}
            isNew={!id}
            refetchQueries={[
              GetMediaDocument,
              GetMediaDownloadUrlDocument,
              GetMediaGridDocument,
              GetMediaUploadUrlDocument,
            ]}
            title="Media Library"
            variablesCreate={mediaState}
            variablesUpdate={mediaState}
            variablesDelete={{ id }}
            onInflight={(inflight) => {
              setDisabled(inflight);
            }}
          />
        </div>
      </Card>
    </div>
  );
}
